/**
 * 📊 HEARTBEATS DASHBOARD - Tableau de bord visuel des 20 emails
 * Suivi des performances : taux d'ouverture, clics, conversions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Users, TrendingUp, Clock, CheckCircle, XCircle,
  RefreshCw, Send, Eye, Calendar, BarChart3, Heart,
  Play, ChevronDown, ChevronUp, AlertCircle, Target,
  MousePointer, UserCheck, Zap, Gift, Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API pour l'autorépondeur Heartbeats
const heartbeatsApi = {
  getStats: async () => {
    const response = await fetch(`${API_URL}/api/heartbeats-autoresponder/prospects/stats`);
    return response.json();
  },
  getProspects: async (status = null) => {
    const url = status 
      ? `${API_URL}/api/heartbeats-autoresponder/prospects?status=${status}`
      : `${API_URL}/api/heartbeats-autoresponder/prospects`;
    const response = await fetch(url);
    return response.json();
  },
  runManually: async () => {
    const response = await fetch(`${API_URL}/api/heartbeats-autoresponder/autoresponder/run`, {
      method: 'POST'
    });
    return response.json();
  }
};

// Séquence des 20 emails
const EMAIL_SEQUENCE = [
  { day: 0, type: 'welcome', label: 'Bienvenue', icon: '💜', color: 'purple' },
  { day: 3, type: 'reminder_j3', label: 'Rappel J3', icon: '💭', color: 'pink' },
  { day: 6, type: 'reminder_j6', label: 'Compatibilité', icon: '⚡', color: 'yellow' },
  { day: 9, type: 'teaser_j9', label: 'Teaser', icon: '👀', color: 'cyan' },
  { day: 12, type: 'social_j12', label: 'Success Story', icon: '💕', color: 'pink' },
  { day: 15, type: 'feature_j15', label: 'Nouveau Salon', icon: '🎉', color: 'cyan' },
  { day: 18, type: 'urgency_j18', label: 'Urgence', icon: '⏰', color: 'red' },
  { day: 21, type: 'local_j21', label: 'Local', icon: '📍', color: 'green' },
  { day: 24, type: 'pixie_j24', label: 'Pixie', icon: '🧚', color: 'pink' },
  { day: 27, type: 'event_j27', label: 'Événement', icon: '🎊', color: 'yellow' },
  { day: 30, type: 'bonus_j30', label: 'Bonus HBT', icon: '🎁', color: 'purple' },
  { day: 33, type: 'story_j33', label: 'Histoire', icon: '❤️', color: 'red' },
  { day: 36, type: 'tip_j36', label: 'Conseil', icon: '💡', color: 'cyan' },
  { day: 39, type: 'reactivate_j39', label: 'Réactivation', icon: '🔔', color: 'cyan' },
  { day: 42, type: 'exclusive_j42', label: 'Exclusif', icon: '🌟', color: 'yellow' },
  { day: 45, type: 'community_j45', label: 'Communauté', icon: '🤝', color: 'pink' },
  { day: 48, type: 'miss_j48', label: 'Tu manques', icon: '😢', color: 'purple' },
  { day: 51, type: 'last_j51', label: 'Dernière chance', icon: '⏳', color: 'red' },
  { day: 54, type: 'goodbye_j54', label: 'Au revoir', icon: '👋', color: 'gray' },
  { day: 60, type: 'final_j60', label: 'Final', icon: '💜', color: 'purple' },
];

// Carte stat principale
const StatCard = ({ icon: Icon, label, value, subValue, color, trend }) => {
  const colorMap = {
    purple: 'from-purple-500/20 to-pink-500/20 border-purple-500/30 text-purple-400',
    pink: 'from-pink-500/20 to-red-500/20 border-pink-500/30 text-pink-400',
    green: 'from-green-500/20 to-emerald-500/20 border-green-500/30 text-green-400',
    cyan: 'from-cyan-500/20 to-blue-500/20 border-cyan-500/30 text-cyan-400',
    yellow: 'from-yellow-500/20 to-orange-500/20 border-yellow-500/30 text-yellow-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      className={`bg-gradient-to-br ${colorMap[color]} rounded-2xl p-5 border backdrop-blur-xl`}
    >
      <div className="flex items-start justify-between">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorMap[color]} flex items-center justify-center border ${colorMap[color].split(' ').slice(2).join(' ')}`}>
          <Icon className="w-6 h-6" />
        </div>
        {trend && (
          <span className={`text-xs px-2 py-1 rounded-full ${trend > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-3xl font-black text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
        <p className="text-sm text-gray-400">{label}</p>
        {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
      </div>
    </motion.div>
  );
};

// Barre de progression des 20 emails
const EmailSequenceProgress = ({ prospects }) => {
  // Calculer les stats par email
  const emailStats = EMAIL_SEQUENCE.map(email => {
    const sent = prospects.filter(p => 
      p.emails_sent?.some(e => e.email_type === email.type)
    ).length;
    return { ...email, sent };
  });

  const totalProspects = prospects.length || 1;

  return (
    <div className="bg-[#12121a] rounded-2xl p-6 border border-purple-500/20">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-purple-400" />
        Progression de la séquence (20 emails sur 60 jours)
      </h3>
      
      <div className="grid grid-cols-10 gap-2 mb-4">
        {emailStats.slice(0, 10).map((email, index) => (
          <EmailProgressItem key={email.type} email={email} total={totalProspects} index={index} />
        ))}
      </div>
      <div className="grid grid-cols-10 gap-2">
        {emailStats.slice(10, 20).map((email, index) => (
          <EmailProgressItem key={email.type} email={email} total={totalProspects} index={index + 10} />
        ))}
      </div>

      {/* Légende */}
      <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-purple-500" /> Envoyé
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-gray-700" /> En attente
        </span>
      </div>
    </div>
  );
};

// Item de progression email
const EmailProgressItem = ({ email, total, index }) => {
  const percentage = total > 0 ? Math.round((email.sent / total) * 100) : 0;
  
  const colorMap = {
    purple: 'bg-purple-500',
    pink: 'bg-pink-500',
    yellow: 'bg-yellow-500',
    cyan: 'bg-cyan-500',
    red: 'bg-red-500',
    green: 'bg-green-500',
    gray: 'bg-gray-500',
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05 }}
      className="text-center group relative"
    >
      {/* Barre de progression verticale */}
      <div className="h-16 w-full bg-gray-800 rounded-lg overflow-hidden relative">
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: `${percentage}%` }}
          transition={{ duration: 0.5, delay: index * 0.05 }}
          className={`absolute bottom-0 w-full ${colorMap[email.color]}`}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg">{email.icon}</span>
        </div>
      </div>
      
      {/* Label */}
      <p className="text-[10px] text-gray-500 mt-1 truncate">J+{email.day}</p>
      
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-black/90 rounded-lg text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
        <p className="font-bold">{email.label}</p>
        <p className="text-gray-400">{email.sent} envoyés ({percentage}%)</p>
      </div>
    </motion.div>
  );
};

// Carte funnel de conversion
const ConversionFunnel = ({ stats }) => {
  const steps = [
    { label: 'Inscrits', value: stats?.total || 0, color: 'purple', icon: Users },
    { label: 'En attente', value: stats?.pending || 0, color: 'yellow', icon: Clock },
    { label: 'Confirmés', value: stats?.active || 0, color: 'green', icon: CheckCircle },
    { label: 'Convertis', value: stats?.converted || 0, color: 'pink', icon: Heart },
  ];

  const maxValue = Math.max(...steps.map(s => s.value), 1);

  return (
    <div className="bg-[#12121a] rounded-2xl p-6 border border-purple-500/20">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Target className="w-5 h-5 text-pink-400" />
        Funnel de Conversion
      </h3>
      
      <div className="space-y-3">
        {steps.map((step, index) => (
          <motion.div
            key={step.label}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-center gap-3"
          >
            <step.icon className={`w-5 h-5 text-${step.color}-400`} />
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">{step.label}</span>
                <span className="text-white font-bold">{step.value}</span>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(step.value / maxValue) * 100}%` }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`h-full bg-${step.color}-500 rounded-full`}
                  style={{ 
                    background: step.color === 'purple' ? 'linear-gradient(90deg, #a855f7, #ec4899)' :
                               step.color === 'yellow' ? 'linear-gradient(90deg, #eab308, #f97316)' :
                               step.color === 'green' ? 'linear-gradient(90deg, #22c55e, #10b981)' :
                               'linear-gradient(90deg, #ec4899, #f43f5e)'
                  }}
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Taux de conversion */}
      <div className="mt-4 pt-4 border-t border-gray-800">
        <div className="flex justify-between items-center">
          <span className="text-gray-400 text-sm">Taux de confirmation</span>
          <span className="text-2xl font-black text-green-400">{stats?.confirmation_rate || 0}%</span>
        </div>
        <div className="flex justify-between items-center mt-2">
          <span className="text-gray-400 text-sm">Taux de conversion</span>
          <span className="text-2xl font-black text-pink-400">{stats?.conversion_rate || 0}%</span>
        </div>
      </div>
    </div>
  );
};

// Composant principal
export default function HeartbeatsDashboard() {
  const [stats, setStats] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningBatch, setRunningBatch] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statsData, prospectsData] = await Promise.all([
        heartbeatsApi.getStats(),
        heartbeatsApi.getProspects()
      ]);
      
      if (statsData.success) setStats(statsData.stats);
      if (prospectsData.success) setProspects(prospectsData.prospects || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const runBatch = async () => {
    setRunningBatch(true);
    try {
      const result = await heartbeatsApi.runManually();
      if (result.success) {
        toast.success(`📧 ${result.stats?.total_sent || 0} emails envoyés !`);
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'exécution');
    } finally {
      setRunningBatch(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Heart className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-white">💜 Dashboard Heartbeats</h2>
            <p className="text-sm text-gray-400">Autorépondeur 20 emails sur 60 jours</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={loadData}
            variant="outline"
            className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button
            onClick={runBatch}
            disabled={runningBatch}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
          >
            {runningBatch ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Exécuter le batch
          </Button>
        </div>
      </div>

      {/* Stats principales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Prospects"
          value={stats?.total || 0}
          subValue="Inscrits via /rencontres"
          color="purple"
        />
        <StatCard
          icon={CheckCircle}
          label="Confirmés"
          value={stats?.active || 0}
          subValue={`${stats?.confirmation_rate || 0}% de confirmation`}
          color="green"
        />
        <StatCard
          icon={Heart}
          label="Convertis"
          value={stats?.converted || 0}
          subValue="Profil créé sur Heartbeats"
          color="pink"
        />
        <StatCard
          icon={Mail}
          label="Emails envoyés"
          value={stats?.emails_sent_total || 0}
          subValue={`${stats?.emails_sent_today || 0} aujourd'hui`}
          color="cyan"
        />
      </div>

      {/* Progression des 20 emails */}
      <EmailSequenceProgress prospects={prospects} />

      {/* Funnel + Stats avancées */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ConversionFunnel stats={stats} />
        
        {/* Stats détaillées */}
        <div className="bg-[#12121a] rounded-2xl p-6 border border-purple-500/20">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            Statistiques Détaillées
          </h3>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-yellow-400" />
                <span className="text-gray-300">En attente de confirmation</span>
              </div>
              <span className="text-xl font-bold text-yellow-400">{stats?.pending || 0}</span>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-red-500/10 rounded-xl border border-red-500/20">
              <div className="flex items-center gap-3">
                <XCircle className="w-5 h-5 text-red-400" />
                <span className="text-gray-300">Désabonnés</span>
              </div>
              <span className="text-xl font-bold text-red-400">{stats?.unsubscribed || 0}</span>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-blue-500/10 rounded-xl border border-blue-500/20">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-blue-400" />
                <span className="text-gray-300">Séquence terminée</span>
              </div>
              <span className="text-xl font-bold text-blue-400">{stats?.completed || 0}</span>
            </div>

            {/* Prochaine exécution */}
            <div className="p-3 bg-purple-500/10 rounded-xl border border-purple-500/20">
              <div className="flex items-center gap-2 text-purple-400 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-medium">Prochaine exécution</span>
              </div>
              <p className="text-white font-bold">Tous les jours à 9h (Paris)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des prospects récents */}
      <div className="bg-[#12121a] rounded-2xl p-6 border border-purple-500/20">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-400" />
          Prospects Récents ({prospects.length})
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-800">
                <th className="pb-3 pr-4">Prénom</th>
                <th className="pb-3 pr-4">Email</th>
                <th className="pb-3 pr-4">Statut</th>
                <th className="pb-3 pr-4">Emails</th>
                <th className="pb-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {prospects.slice(0, 10).map((prospect) => (
                <tr key={prospect.id} className="text-sm">
                  <td className="py-3 pr-4 font-medium text-white">{prospect.first_name}</td>
                  <td className="py-3 pr-4 text-gray-400">{prospect.email}</td>
                  <td className="py-3 pr-4">
                    <StatusBadge status={prospect.status} />
                  </td>
                  <td className="py-3 pr-4 text-gray-400">
                    {prospect.emails_sent?.length || 0}/20
                  </td>
                  <td className="py-3 text-gray-500 text-xs">
                    {new Date(prospect.created_at).toLocaleDateString('fr-FR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Badge statut
const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    active: 'bg-green-500/20 text-green-400 border-green-500/30',
    converted: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    unsubscribed: 'bg-red-500/20 text-red-400 border-red-500/30',
    completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
  };
  
  const labels = {
    pending: '⏳ En attente',
    active: '✅ Confirmé',
    converted: '💜 Converti',
    unsubscribed: '🚫 Désabonné',
    completed: '✓ Terminé'
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.active}`}>
      {labels[status] || status}
    </span>
  );
};
